import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import TopBar from '../components/TopBar';
import { useLang } from '../LangContext';
import { useT } from '../i18n';
import { getBarcodeStatus } from '../api';

function formatTime(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function BarcodeDisplayScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const t = useT(lang);

  // 從 RedemptionConfirmScreen 傳來的 state
  const {
    uuid,
    remainingSeconds: initialTTL = 300,
    memberName = '',
    mode = 'points',
  } = location.state || {};

  const [seconds, setSeconds] = useState(initialTTL);
  const [expired, setExpired] = useState(false);
  const [dispensed, setDispensed] = useState(false);

  // 如果沒有 UUID（直接進來），導回確認畫面
  useEffect(() => {
    if (!uuid) {
      navigate('/confirm?mode=points', { replace: true });
    }
  }, [uuid, navigate]);

  // 倒數計時
  useEffect(() => {
    if (expired || dispensed || !uuid) return;
    const id = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 1) {
          clearInterval(id);
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [expired, dispensed, uuid]);

  // 輪詢條碼狀態（每 2 秒）
  useEffect(() => {
    if (!uuid || expired || dispensed) return;
    const poll = setInterval(async () => {
      try {
        const data = await getBarcodeStatus(uuid);
        if (data.status === 'dispensed') {
          setDispensed(true);
          clearInterval(poll);
          setTimeout(() => navigate('/success'), 800);
        } else if (data.status === 'expired') {
          setExpired(true);
          clearInterval(poll);
        }
      } catch {
        // 網路錯誤，繼續輪詢
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [uuid, expired, dispensed, navigate]);

  // 過期後導航
  const handleExpiredNav = useCallback(() => {
    navigate('/error?type=expired');
  }, [navigate]);

  useEffect(() => {
    if (expired) {
      const id = setTimeout(handleExpiredNav, 2000);
      return () => clearTimeout(id);
    }
  }, [expired, handleExpiredNav]);

  if (!uuid) return null;

  const pct = ((initialTTL - seconds) / initialTTL) * 100;
  const timerColor = seconds > 60 ? '#FFD166' : seconds > 30 ? '#FF9F45' : '#FF5C36';
  const modeLabel = mode === 'token' ? '活動資格兌換' : '點數兌換';

  return (
    <div style={{
      backgroundColor: '#1A1A2E',
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      color: '#ffffff',
      fontFamily: "'Be Vietnam Pro', sans-serif",
    }}>
      <TopBar showBack dark title="泡泡茶 BubbleCha" />

      {/* Orange instruction strip */}
      <div style={{
        marginTop: 56,
        backgroundColor: '#FF5C35',
        padding: '10px 0',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <span style={{ fontSize: 13, color: '#ffffff', fontWeight: 500, letterSpacing: '0.02em' }}>
          {t('scanInstruction')}
        </span>
      </div>

      <main style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 32px 48px',
        gap: 32,
      }}>

        {/* ── QR Card ─────────────────────────────── */}
        {dispensed ? (
          /* 出球成功畫面 */
          <div style={{
            backgroundColor: 'rgba(61,191,160,0.15)',
            borderRadius: 24,
            padding: 40,
            width: '100%', maxWidth: 300,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            border: '1px solid rgba(61,191,160,0.4)',
          }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="#3DBFA0" style={{ marginBottom: 16 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 18, textAlign: 'center', marginBottom: 8,
            }}>出球成功！</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textAlign: 'center', lineHeight: 1.6 }}>
              正在跳轉...
            </p>
          </div>
        ) : !expired ? (
          /* 正常 QR Code 顯示 */
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: 24,
            padding: 24,
            width: '100%', maxWidth: 300,
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            {/* 真實 QR Code */}
            <div style={{
              border: '1px solid #e8e5ff',
              padding: 4,
              borderRadius: 4,
            }}>
              <QRCodeSVG
                value={uuid}
                size={200}
                fgColor="#1A1A2E"
                bgColor="#ffffff"
                level="M"
              />
            </div>

            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <p style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700, fontSize: 13, color: '#1A1A2E', marginBottom: 4,
              }}>{memberName || t('userName')}</p>
              <p style={{ fontSize: 11, color: 'rgba(90,89,113,0.7)', fontWeight: 500 }}>
                {modeLabel}
              </p>
            </div>
          </div>
        ) : (
          /* 過期畫面 */
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 24,
            padding: 40,
            width: '100%', maxWidth: 300,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            border: '1px solid rgba(255,92,54,0.3)',
          }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="#FF5C36" style={{ marginBottom: 16 }}>
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
            </svg>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 18, textAlign: 'center', marginBottom: 8,
            }}>{t('barcodeExpiredMsg')}</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textAlign: 'center', lineHeight: 1.6 }}>
              {t('barcodeExpiredDesc')}
            </p>
          </div>
        )}

        {/* ── Timer ───────────────────────────────── */}
        {!dispensed && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            width: '100%',
          }}>
            <p style={{
              fontSize: 11, color: 'rgba(255,255,255,0.7)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 0,
            }}>{t('barcodeValidTime')}</p>

            <span style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800, fontSize: 52, color: timerColor,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
              transition: 'color 0.5s',
            }}>
              {formatTime(seconds)}
            </span>

            {/* Progress bar */}
            <div style={{
              width: '100%', maxWidth: 280, height: 3,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: 9999, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                backgroundColor: timerColor,
                borderRadius: 9999,
                transition: 'width 1s linear, background-color 0.5s',
              }}/>
            </div>

            {/* Tip pill */}
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              padding: '8px 16px',
              borderRadius: 9999,
              display: 'flex', alignItems: 'center', gap: 8,
              border: '1px solid rgba(255,255,255,0.05)',
              maxWidth: 320,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.7)">
                <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
              </svg>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                {t('brightnessNote')}
              </span>
            </div>
          </div>
        )}

      </main>

      {/* ── Bottom action ────────────────────────── */}
      <div style={{ padding: '0 32px 40px', maxWidth: 390, margin: '0 auto', width: '100%' }}>
        {expired ? (
          <button
            onClick={() => navigate('/confirm?mode=' + mode)}
            style={{
              width: '100%', height: 52,
              backgroundColor: '#FF5C36', color: '#ffffff',
              border: 'none', borderRadius: 14,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            {t('regenerate')}
          </button>
        ) : !dispensed && (
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%', height: 52,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#ffffff',
              borderRadius: 14,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            取消 / 返回首頁
          </button>
        )}
      </div>
    </div>
  );
}
